import {
  differenceInCalendarDays,
  differenceInDays,
  differenceInYears,
  format,
  formatDistanceStrict,
  parseISO,
  subDays,
} from 'date-fns';
/**
 *
 * @param date
 * @returns formatted Date string in the form of `yyyy-MM-dd`
 */
export function simpleDashDate(date?: string | Date | null) {
  const pdate = parseDate(date);
  if (!pdate) return null;
  if (isNaN(pdate.getTime())) return null;
  return format(pdate, 'yyyy-MM-dd');
}
/**
 *
 * @param date
 * @returns formatted Date string in the form of `MM/dd/yyyy`
 */
export function simpleDate(date?: string | Date | null) {
  const pdate = parseDate(date);
  if (!pdate) return null;
  if (isNaN(pdate.getTime())) return null;
  return format(pdate, 'MM/dd/yyyy');
}

/**
 * @param date a date of birth
 * @returns age in whole years as of today, or null for missing/unparseable input
 */
export function calculateAge(date?: string | Date | null): number | null {
  const pdate = parseDate(date);
  if (!pdate || isNaN(pdate.getTime())) return null;
  return differenceInYears(new Date(), pdate);
}

/**
 * Elapsed-time label relative to a base "now" (pass `useNow()` so it ticks on its own cadence).
 * Uses the strict variant so output matches the design copy exactly ("1 hour ago", not
 * "about 1 hour ago"). Returns null for missing/unparseable input.
 * @param date the past date
 * @param now the reference point (defaults to the current time)
 * @returns e.g. "1 hour ago"
 */
export function relativeTimeFromNow(date?: string | Date | null, now: Date = new Date()) {
  const pdate = parseDate(date);
  if (!pdate || isNaN(pdate.getTime())) return null;
  return formatDistanceStrict(pdate, now, { addSuffix: true });
}

/**
 * Formats to a date string with time
 * @param date
 * @returns A string containing the date and time. Example: "04/10/2024 at 12:00 AM"
 */
export const simpleDateAtTime = (date: string | Date) => {
  const pdate = parseDate(date);
  if (!pdate) return null;
  if (isNaN(pdate.getTime())) return null;

  return format(pdate, "MM/dd/yyyy 'at' h:mm a");
};

// With seconds — for feeds where many events land inside a single minute
// (e.g. the referral activity log's automated bursts).
export const simpleDateAtTimeWithSeconds = (date: string | Date) => {
  const pdate = parseDate(date);
  if (!pdate) return null;
  if (isNaN(pdate.getTime())) return null;

  return format(pdate, "MM/dd/yyyy 'at' h:mm:ss a");
};

/**
 * @param date
 * @returns formatted Date string depending on the relative difference between the date and the current date
 */
export function dateAtTime(date?: string | Date | null) {
  const pdate = parseDate(date);
  if (!pdate) return null;
  if (isNaN(pdate.getTime())) return null;
  const reldif = differenceInCalendarDays(pdate, new Date());
  if (reldif == 0) {
    return format(pdate, "'Today at' h:mm a");
  }
  if (reldif == 1) {
    return format(pdate, "'Tomorrow at' h:mm a");
  }
  if (reldif == -1) {
    return format(pdate, "'Yesterday at' h:mm a");
  }
  return format(pdate, "MM/dd/yyyy 'at' h:mm a");
}
/**
 *
 * @param date
 * @returns formatted Date string in the form of `MM/dd/yyyy 'at' h:mm a`
 */
export function dateAtTimeNoRelative(date?: string | Date | null) {
  const pdate = parseDate(date);
  if (!pdate) return null;
  if (isNaN(pdate.getTime())) return null;
  return format(pdate, "MM/dd/yyyy 'at' h:mm a");
}

export function dateWithoutTimezone(date?: string | Date | null) {
  return simpleDate(date);
  //need more information about what the difference is supposed to be,
  //since neither function printed a timezone.
}
/**
 * @param date
 * @returns formatted Date string in the form of `yyyy-MM-dd'T'HH:mm:ss.SSS'Z'`
 */
export function parseDate(date?: string | Date | null) {
  if (!date) return null;
  if (date instanceof Date) return date;
  try {
    const parsed = parseISO(date);
    if (isNaN(parsed.getTime())) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * @param start
 * @param end
 * @returns object with start and end dates for the prior period
 */
export function calculatePriorPeriod(start: Date, end: Date) {
  const priorEnd = subDays(start, 1); // one day before start of current period
  const priorStart = subDays(priorEnd, differenceInDays(end, start)); // same difference in days as current period

  return {
    start: priorStart,
    end: priorEnd,
  };
}

// "12 mins" under an hour ("1 min" singular); "1 hr 3 mins" under a day; "3 days" / "3 days 4 hrs"
// beyond. Coarse on purpose — it's an at-a-glance age. Rolling over to days at 24 hours keeps a
// long-running span readable: "336 hr" makes the reader do the division. Works for a finished span
// too: pass its end as `now`.
export function elapsed(date: Date, now: Date): string {
  const minutes = Math.max(0, Math.floor((now.getTime() - date.getTime()) / 60000));
  if (minutes < 60) return `${minutes} min${minutes === 1 ? '' : 's'}`;
  if (minutes < 24 * 60) {
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    const hourPart = `${hours} hr${hours === 1 ? '' : 's'}`;
    return rest === 0 ? hourPart : `${hourPart} ${rest} min${rest === 1 ? '' : 's'}`;
  }
  const days = Math.floor(minutes / (24 * 60));
  const hours = Math.floor((minutes % (24 * 60)) / 60);
  const dayPart = `${days} day${days === 1 ? '' : 's'}`;
  return hours === 0 ? dayPart : `${dayPart} ${hours} hr${hours === 1 ? '' : 's'}`;
}
